import { useCallback } from 'react'
import { mdiClose } from '@mdi/js'
import { useTranslation } from 'react-i18next'
import { fetchMembers } from '../../api/multiOrg'
import { ApiSearchableSelect, type ApiSearchableSelectOption } from '../ApiSearchableSelect'
import { AssetPicker } from '../AssetPicker'
import { Badge } from '../ui/badge'
import { AppIcon } from '../ui/icon'

export interface ScopeAsset {
    uid: string
    name: string
}

/** The perimeter: assets picked one by one, each removable. */
export function ScopePicker({
    org,
    value,
    onChange,
    testId = 'campaign-scope',
}: {
    org: string
    value: ScopeAsset[]
    onChange: (next: ScopeAsset[]) => void
    testId?: string
}) {
    const { t } = useTranslation('campaigns')
    return (
        <div className="space-y-2" data-testid={testId}>
            <AssetPicker
                org={org}
                testId={`${testId}-picker`}
                placeholder={t('scope_placeholder')}
                onChange={(uid, name) => {
                    if (!value.some((a) => a.uid === uid)) onChange([...value, { uid, name }])
                }}
            />
            {value.length === 0 ? (
                <p className="text-sm text-muted-foreground">{t('scope_empty')}</p>
            ) : (
                <div className="flex flex-wrap gap-2">
                    {value.map((a) => (
                        <Badge key={a.uid} variant="secondary" className="gap-1" data-testid={`${testId}-chip-${a.name}`}>
                            {a.name}
                            <button
                                type="button"
                                className="rounded hover:bg-muted"
                                aria-label={t('remove_named', { name: a.name })}
                                title={t('remove_named', { name: a.name })}
                                onClick={() => onChange(value.filter((x) => x.uid !== a.uid))}
                                data-testid={`${testId}-remove-${a.name}`}
                            >
                                <AppIcon path={mdiClose} size="xs" />
                            </button>
                        </Badge>
                    ))}
                </div>
            )}
        </div>
    )
}

/** A campaign agent: a member, with the name the chip shows. */
export interface CampaignAgent {
    uid: string
    name: string
}

/**
 * The agents: members searched live through the API (`q` on /members), never a
 * client-side list cut at its first page, like the contract stakeholder picker.
 * Each pick becomes a removable chip.
 */
export function AgentsPicker({
    org,
    value,
    onChange,
    testId = 'campaign-agents',
}: {
    org: string
    value: CampaignAgent[]
    onChange: (next: CampaignAgent[]) => void
    testId?: string
}) {
    const { t } = useTranslation('campaigns')
    const search = useCallback(
        async (q: string): Promise<ApiSearchableSelectOption[]> => {
            const page = await fetchMembers(org, { q: q || undefined, limit: 20 })
            return page.data
                .filter((m) => !value.some((a) => a.uid === m.user.uid))
                .map((m) => ({
                    value: m.user.uid,
                    label: m.user.name && m.user.email ? `${m.user.name} · ${m.user.email}` : m.user.name || m.user.email || m.user.uid,
                    data: { name: m.user.name || m.user.email || m.user.uid },
                }))
        },
        [org, value],
    )
    return (
        <div className="space-y-2" data-testid={testId}>
            <ApiSearchableSelect
                value=""
                onSearch={search}
                minSearchLength={0}
                placeholder={t('agents_placeholder')}
                searchPlaceholder={t('agents_search_placeholder')}
                emptyMessage={t('agents_no_match')}
                onOptionSelect={(option) => {
                    if (option.value && !value.some((a) => a.uid === option.value)) {
                        onChange([...value, { uid: option.value, name: String(option.data?.name ?? option.label) }])
                    }
                }}
                data-testid={`${testId}-picker`}
            />
            {value.length === 0 ? (
                <p className="text-sm text-muted-foreground">{t('agents_empty')}</p>
            ) : (
                <div className="flex flex-wrap gap-2">
                    {value.map((a) => (
                        <Badge key={a.uid} variant="secondary" className="gap-1" data-testid={`${testId}-chip-${a.name}`}>
                            {a.name}
                            <button
                                type="button"
                                className="rounded hover:bg-muted"
                                aria-label={t('remove_named', { name: a.name })}
                                title={t('remove_named', { name: a.name })}
                                onClick={() => onChange(value.filter((x) => x.uid !== a.uid))}
                                data-testid={`${testId}-remove-${a.name}`}
                            >
                                <AppIcon path={mdiClose} size="xs" />
                            </button>
                        </Badge>
                    ))}
                </div>
            )}
        </div>
    )
}
